import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { HiPlus } from "react-icons/hi";
import { FiEdit2, FiTrash2, FiRefreshCw } from "react-icons/fi";
import { useListOnboardingQuery, useDeleteOnboardingMutation } from "../../../features/admin/onboarding/onboardingApi";

const STEP_COUNT = 7;

const STATUS_STYLE = {
  draft: "bg-gray-100 text-gray-600",
  ready: "bg-blue-100 text-blue-700",
  provisioning: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "draft", label: "Drafts" },
  { key: "ready", label: "Ready" },
  { key: "completed", label: "Completed" },
  { key: "failed", label: "Failed" },
];

const OnboardingList = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("access_token");
  const [status, setStatus] = useState("");

  // refetch on every mount/arg change so freshly-saved current_step/status show immediately
  const { data, isFetching, refetch } = useListOnboardingQuery({ token, status }, { refetchOnMountOrArgChange: true });
  const [deleteOnboarding] = useDeleteOnboardingMutation();
  const rows = data?.body || [];

  const remove = async (id, name) => {
    if (!window.confirm(`Delete onboarding "${name}"? This cannot be undone.`)) return;
    try {
      await deleteOnboarding({ token, id }).unwrap();
      toast.success("Onboarding deleted");
      refetch();
    } catch {
      toast.error("Could not delete onboarding");
    }
  };

  return (
    <div className="page-content bg-gray-50 min-h-screen">
      <div className="w-full px-4 md:px-8 py-6">
        {/* header */}
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Client Onboardings</h1>
            <p className="text-sm text-gray-500 mt-0.5">In-progress and past onboardings. Completed ones also appear in Clients.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => refetch()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
              <FiRefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button type="button" onClick={() => navigate("/onboardClient")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm">
              <HiPlus className="h-4 w-4" /> New onboarding
            </button>
          </div>
        </div>

        {/* filters */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setStatus(f.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${status === f.key ? "bg-blue-600 border-blue-600 text-white" : "border-gray-300 text-gray-600 hover:border-blue-400"}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* table */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left font-medium px-4 py-3">Name</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3">Progress</th>
                <th className="text-left font-medium px-4 py-3">Tenant</th>
                <th className="text-left font-medium px-4 py-3">Updated</th>
                <th className="text-left font-medium px-4 py-3">By</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                  {isFetching ? "Loading…" : "No onboardings yet. Start one with “New onboarding”."}
                </td></tr>
              )}
              {rows.map((r) => {
                const done = r.status === "completed";
                const completed = Array.isArray(r.step_status)
                  ? r.step_status.filter((s) => s?.status === "complete").length
                  : 0;
                return (
                  <tr key={r.onboarding_id} className="border-t border-gray-100 hover:bg-gray-50/60">
                    <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLE[r.status] || "bg-gray-100 text-gray-600"}`}>{r.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full bg-blue-600" style={{ width: `${(completed / STEP_COUNT) * 100}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">{completed}/{STEP_COUNT} done</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.tenant_id ? `tenant_${r.tenant_id}` : "—"}</td>
                    <td className="px-4 py-3 text-gray-500">{r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}</td>
                    <td className="px-4 py-3 text-gray-500">{r.updated_by_name || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button type="button" onClick={() => navigate(`/onboardClient?id=${r.onboarding_id}`)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50">
                          <FiEdit2 className="h-3.5 w-3.5" /> {done ? "View" : "Resume"}
                        </button>
                        <button type="button" onClick={() => remove(r.onboarding_id, r.name)}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-300 bg-white text-gray-400 hover:text-red-500 hover:border-red-300">
                          <FiTrash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OnboardingList;
